<template>
  <div id="upload" class="container">
    <div v-if="isTestMode" class="test-banner">
      TEST MODE — this upload will be flagged and excluded from real results
    </div>
    <h3 class="title is-4">Upload Proxy</h3>
    <div class="columns">
      <div class="column is-three-fifths">
        <div v-show="!uploading && !uploaded" class="upload_area">
          <FileUpload
            v-if="canUpload"
            mode="basic"
            name="proxy"
            accept="application/pdf,image/*"
            :auto="true"
            :custom-upload="true"
            choose-label="Drop or select file"
            @uploader="onUpload"
          />
          <div v-else class="upload-blocked">
            <i class="pi pi-info-circle" />
            <p>
              Please fill in the custodian shareholder's information on the
              right before uploading.
            </p>
          </div>
          <p class="drop-hint">
            <i class="pi pi-file" style="font-size: 40px;" />
          </p>
          <p>Select your signed proxy form (PDF or image).</p>
        </div>
        <ProgressBar v-show="uploading" :value="progress" />
        <div v-show="uploaded" class="uploaded">
          <h4 class="subtitle is-4">Uploaded</h4>
          <i class="pi pi-check check" />
          <p>We have received your proxy.</p>
        </div>
      </div>
      <div class="column is-two-fifths">
        <h5 class="title is-3">Instructions</h5>
        <p>
          Fill out your paper proxy form according to the instructions indicated on the
          form. Scan or photograph your signed proxy form.
        </p>
        <br />
        <div class="field">
          <Checkbox v-model="signcustodian" :binary="true" input-id="custodian" />
          <label for="custodian" style="margin-left: 8px;">
            I am uploading as a custodian.
          </label>
        </div>

        <div v-if="signcustodian" class="custodian-fields">
          <p class="muted">
            Tell us who you're uploading for so we can apply this proxy to
            the correct shareholder record.
          </p>
          <div class="p-field">
            <label>Custodian for</label>
            <InputText v-model="custodianName" placeholder="Full name" fluid />
          </div>
          <div class="p-field">
            <label>Their registration number</label>
            <InputText
              v-model="custodianShn"
              placeholder="Shareholder number"
              fluid
            />
          </div>
          <div class="p-field">
            <label>Total shares</label>
            <InputNumber
              v-model="custodianShares"
              :min-fraction-digits="0"
              :max-fraction-digits="4"
              fluid
            />
          </div>
        </div>

        <br />
        <h5 class="title is-5">To upload:</h5>
        <p>Select the file from your computer using the button on this page.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import FileUpload from 'primevue/fileupload'
import ProgressBar from 'primevue/progressbar'
import Checkbox from 'primevue/checkbox'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from 'firebase/storage'
import { addDoc, serverTimestamp } from 'firebase/firestore'
import { storage, auth, votesCollection, mail } from '@/firebase'
import { useUserStore } from '@/stores/user'
import { useMeetingStore } from '@/stores/meeting'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const userStore = useUserStore()
const meetingStore = useMeetingStore()

const testShareholder = { name: 'Test Voter', shn: 9999999, shares: 100 }
const isTestMode = computed(() => route.query.test === 'true' && userStore.isAdmin)
const activeShareholder = computed(() =>
  isTestMode.value ? testShareholder : userStore.profile,
)

const uploading = ref(false)
const uploaded = ref(false)
const progress = ref(0)
const signcustodian = ref(false)
const custodianName = ref('')
const custodianShn = ref('')
const custodianShares = ref(null)

// Require full custodial info before allowing a file to be selected —
// otherwise the upload ends up in the votes collection with no way to
// tie it back to the shareholder whose proxy is being cast.
const canUpload = computed(() => {
  if (!signcustodian.value) return true
  return (
    !!custodianName.value.trim() &&
    !!String(custodianShn.value || '').trim() &&
    Number.isFinite(custodianShares.value) &&
    custodianShares.value > 0
  )
})

function onUpload(ev) {
  const file = ev.files && ev.files[0]
  if (!file) return
  if (!canUpload.value) {
    toast.add({
      severity: 'warn',
      summary: 'Custodian info required',
      detail: 'Fill in the custodian shareholder information first.',
      life: 3000,
    })
    return
  }
  uploaded.value = false
  uploading.value = true

  // Namespace under the user's uid + a random+timestamp prefix so the same
  // filename uploaded twice never collides and trips an update-denied rule.
  const uid = auth.currentUser.uid
  const rand = Math.random().toString(36).slice(2, 10)
  const safeName = (file.name || 'proxy').replace(/[^a-zA-Z0-9._-]/g, '_')
  const path = `proxy/${uid}/${Date.now()}-${rand}-${safeName}`
  const task = uploadBytesResumable(storageRef(storage, path), file)

  task.on(
    'state_changed',
    (snap) => {
      progress.value = Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
    },
    (err) => {
      console.error(err)
      toast.add({
        severity: 'error',
        summary: 'Upload failed',
        detail: 'Please refresh and try again.',
        life: 5000,
      })
      uploading.value = false
    },
    async () => {
      const downloadURL = await getDownloadURL(task.snapshot.ref)
      uploaded.value = true
      uploading.value = false
      progress.value = 0

      const sh = activeShareholder.value
      const voteBody = {
        name: sh.name,
        registrationnum: sh.shn,
        totalshares: sh.shares,
        signcustodian: signcustodian.value,
      }
      if (signcustodian.value) {
        voteBody.custodian = custodianName.value.trim()
        voteBody.custodianshn = String(custodianShn.value || '').trim()
        voteBody.custodianShares = Number(custodianShares.value) || 0
      }

      const voteDoc = {
        createdOn: serverTimestamp(),
        url: downloadURL,
        vote: voteBody,
        user: auth.currentUser.uid,
        meeting: route.params.meeting_id,
      }
      if (isTestMode.value) voteDoc.test = true

      await addDoc(votesCollection, voteDoc)

      if (!isTestMode.value) {
        const custodialLine = signcustodian.value
          ? `<p>Custodian for: <strong>${voteBody.custodian}</strong> (SHN ${voteBody.custodianshn}, ${voteBody.custodianShares} shares)</p>`
          : ''
        await addDoc(mail, {
          to: meetingStore.notificationEmails,
          message: {
            subject: `A new vote has been cast for ${sh.name}`,
            text: `${sh.name} has uploaded a proxy. ${downloadURL}`,
            html: `<p>${sh.name} has uploaded a proxy.</p>
              <p>Name: <strong>${sh.name}</strong></p>
              <p>Registration Number: <strong>${sh.shn}</strong></p>
              <p>Total shares: <strong>${sh.shares}</strong></p>
              <p>Custodian: ${signcustodian.value}</p>
              ${custodialLine}
              <a href="${downloadURL}" target="_blank">Click here to view.</a>`,
          },
        })
      }

      setTimeout(() => {
        router.push(isTestMode.value ? '/admin/votes' : '/dashboard')
      }, 1500)
    },
  )
}
</script>

<style lang="scss" scoped>
.test-banner {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 10px 16px;
  text-align: center;
  font-weight: 600;
  margin-bottom: 16px;
}

#upload {
  margin-top: 50px;
  padding: 20px;

  .upload_area {
    text-align: center;
    padding: 60px 20px;
    border: 2px dashed #bbb;
    border-radius: 10px;

    .drop-hint {
      margin-top: 20px;
    }
  }

  .upload-blocked {
    color: var(--muted-text, #666);
    font-size: 0.9rem;
    padding: 12px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    i {
      font-size: 20px;
    }
  }

  .custodian-fields {
    margin-top: 14px;
    padding: 12px 16px;
    border: 1px solid var(--panel-border, #e6e6e6);
    border-radius: 8px;
    background: var(--panel-bg, #fafafa);

    .muted {
      font-size: 0.85rem;
      color: var(--muted-text, #666);
      margin-bottom: 10px;
    }

    .p-field {
      margin-bottom: 10px;

      label {
        display: block;
        font-size: 0.85rem;
        color: var(--muted-text, #555);
        margin-bottom: 4px;
      }
    }
  }

  :deep(.p-inputtext),
  :deep(.p-inputnumber) {
    width: 100%;
  }

  .check {
    color: green;
    font-size: 40px;
  }

  .uploaded {
    text-align: center;
  }
}
</style>
